import { supabase } from '@/lib/supabase';

export type WhatsAppSystemStatusLevel = 'healthy' | 'degraded' | 'stale' | 'unknown';

export interface WhatsAppHeartbeatStatus {
  source: string;
  status: WhatsAppSystemStatusLevel;
  checked_at?: string | null;
  age_seconds?: number | null;
  duration_ms?: number | null;
  detail: {
    service?: string;
    connection?: string;
    registered?: boolean;
    last_message_at?: string | null;
    last_success_at?: string | null;
    messages_captured_since_start?: number;
    last_error?: string | null;
    last_error_at?: string | null;
    started_at?: string | null;
  };
}

export interface WhatsAppSystemStatus {
  generated_at: string;
  status: WhatsAppSystemStatusLevel;
  headline: string;
  heartbeat: WhatsAppHeartbeatStatus;
  latest_message: {
    created_at?: string | null;
    message_at?: string | null;
    age_seconds?: number | null;
    direction?: string | null;
    source?: string | null;
    analysed: boolean;
    ingestion_delay_seconds?: number | null;
  };
  bridge_run: {
    created_at?: string | null;
    kind?: string | null;
    status?: string | null;
    messages_ingested: number;
    has_error: boolean;
  };
  triage: {
    started_at?: string | null;
    completed_at?: string | null;
    age_seconds?: number | null;
    status?: string | null;
    messages_analysed: number;
    action_items_created: number;
    failed_messages: number;
    transient_retries: number;
    recovered_messages: number;
    /** A sweep that has started and not finished yet (migration 20261010620000); the fields above are the last finished one. */
    in_flight_since?: string | null;
  };
  queue: Record<string, unknown>;
  activity_24h: {
    messages: number;
    inbound: number;
    outbound: number;
    unanalysed: number;
    contact_linked: number;
    freshdesk_linked: number;
    action_items_created: number;
  };
  linkage: {
    verified_identities: number;
    pending_identities: number;
    unmatched_identities: number;
    open_action_items: number;
    customer_linked_action_items: number;
    traveller_linked_action_items: number;
    status: 'healthy' | 'partial' | 'unproven' | 'unknown';
  };
  projection: {
    status: 'healthy' | 'degraded' | 'stale' | 'unknown';
    direct_messages: number;
    canonical_events: number;
    ucc_whatsapp_threads: number;
    ucc_whatsapp_messages: number;
    customer_linked_threads: number;
    freshdesk_context_eligible_threads: number;
    verified_lid_aliases: number;
    direct_messages_24h: number;
    canonical_events_24h: number;
    unprojected_messages_24h: number;
    ucc_work_source_identity_missing_24h: number | null;
    /** The 7-day count the status reads (migration 20261010300000). null until that migration is live. */
    ucc_work_source_identity_missing_7d: number | null;
    /** Informational since 20261010300000: a closed gap stays in this count for a month. */
    ucc_work_source_identity_missing_30d: number | null;
    /** Which window drove the coverage status: 7 days, or 30 on a database without the 7-day column. */
    ucc_gap_status_window_days: 7 | 30;
    projection_coverage_24h: number;
    last_direct_message_at?: string | null;
    last_projected_message_at?: string | null;
  };
}

function countOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * UCC source-identity coverage, as the status bar reads it.
 *
 * The status window is 7 days. It was 30, so the 170 September messages that never got a UCC copy (newest
 * 30 September) kept the WhatsApp bullet amber into November although nothing had been missed since. The 30-day
 * count is still shown, as a number. On a database without the 7-day column (before migration 20261010300000) the
 * 30-day count is used, so the bar never claims a coverage it did not measure.
 */
export function deriveUccCoverage(projection: Record<string, unknown>): {
  level: 'healthy' | 'degraded' | 'unknown';
  missing24h: number | null;
  missing7d: number | null;
  missing30d: number | null;
  windowDays: 7 | 30;
} {
  const missing24h = countOrNull(projection.ucc_work_source_identity_missing_24h);
  const missing7d = countOrNull(projection.ucc_work_source_identity_missing_7d);
  const missing30d = countOrNull(projection.ucc_work_source_identity_missing_30d);
  const windowDays: 7 | 30 = missing7d !== null ? 7 : 30;
  const missingWindow = windowDays === 7 ? missing7d : missing30d;
  const level = (missing24h !== null && missing24h > 0) || (missingWindow !== null && missingWindow > 0)
    ? 'degraded'
    : missing24h === null || missingWindow === null
      ? 'unknown'
      : 'healthy';
  return { level, missing24h, missing7d, missing30d, windowDays };
}

export const whatsappSystemStatusService = {
  async getStatus(signal?: AbortSignal): Promise<WhatsAppSystemStatus> {
    const request = supabase.rpc('ops_whatsapp_system_status_get');
    const statusResult = await (signal ? request.abortSignal(signal) : request);
    const { data, error } = statusResult;
    if (error) throw error;
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
      throw new Error('WhatsApp system status returned an invalid response.');
    }
    const raw = data as unknown as Omit<WhatsAppSystemStatus, 'projection'> & {
      projection?: Record<string, unknown>;
    };
    const projection = raw.projection || {};
    const {
      level: coverage, missing24h, missing7d, missing30d, windowDays,
    } = deriveUccCoverage(projection);
    const projectionStatus = (projection.projection_status || 'unknown') as WhatsAppSystemStatus['projection']['status'];
    const status = raw.status === 'healthy' && coverage !== 'healthy' ? coverage : raw.status;
    const headline = raw.status === 'healthy' && coverage === 'degraded'
      ? 'WhatsApp is live, but UCC source identity gaps need review'
      : raw.status === 'healthy' && coverage === 'unknown'
        ? 'WhatsApp UCC source identity coverage is not measured'
        : raw.headline;
    return {
      ...raw,
      status,
      headline,
      linkage: {
        ...raw.linkage,
        status: raw.linkage.status === 'healthy' && coverage !== 'healthy'
          ? coverage === 'degraded' ? 'partial' : 'unknown'
          : raw.linkage.status,
      },
      projection: {
        status: projectionStatus === 'healthy' && coverage !== 'healthy' ? coverage : projectionStatus,
        direct_messages: Number(projection.direct_messages || 0),
        canonical_events: Number(projection.canonical_events || 0),
        ucc_whatsapp_threads: Number(projection.ucc_whatsapp_threads || 0),
        ucc_whatsapp_messages: Number(projection.ucc_whatsapp_messages || 0),
        customer_linked_threads: Number(projection.customer_linked_threads || 0),
        freshdesk_context_eligible_threads: Number(projection.freshdesk_context_eligible_threads || 0),
        verified_lid_aliases: Number(projection.verified_lid_aliases || 0),
        direct_messages_24h: Number(projection.direct_messages_24h || 0),
        canonical_events_24h: Number(projection.canonical_events_24h || 0),
        unprojected_messages_24h: Number(projection.unprojected_messages_24h || 0),
        ucc_work_source_identity_missing_24h: missing24h,
        ucc_work_source_identity_missing_7d: missing7d,
        ucc_work_source_identity_missing_30d: missing30d,
        ucc_gap_status_window_days: windowDays,
        projection_coverage_24h: Number(projection.projection_coverage_24h || 0),
        last_direct_message_at: typeof projection.last_direct_message_at === 'string'
          ? projection.last_direct_message_at
          : null,
        last_projected_message_at: typeof projection.last_projected_message_at === 'string'
          ? projection.last_projected_message_at
          : null,
      },
    };
  },
};
